/**
 * Product Card - Unified JavaScript
 * Handles add-to-cart and quick view functionality
 */

(function() {
  'use strict';

  const ProductCard = {
    selectors: {
      card: '[data-product-card]',
      addBtn: '.product-card__btn--add',
      viewBtn: '.product-card__btn--view'
    },

    classes: {
      loading: 'is-loading',
      success: 'is-success'
    },

    /**
     * Initialize product card functionality
     * @param {Element|Document} scope - Container to search within
     */
    init: function(scope) {
      const container = scope || document;
      this.bindAddToCart(container);
      this.bindQuickView(container);
    },

    /**
     * Bind add to cart button events
     * @param {Element|Document} container
     */
    bindAddToCart: function(container) {
      const buttons = container.querySelectorAll(this.selectors.addBtn);

      buttons.forEach(btn => {
        // Skip if already bound
        if (btn.hasAttribute('data-bound')) return;
        btn.setAttribute('data-bound', 'true');

        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.handleAddToCart(btn);
        });
      });
    },

    /**
     * Bind quick view button events
     * @param {Element|Document} container
     */
    bindQuickView: function(container) {
      const buttons = container.querySelectorAll(this.selectors.viewBtn);

      buttons.forEach(btn => {
        // Skip if already bound
        if (btn.hasAttribute('data-bound')) return;
        btn.setAttribute('data-bound', 'true');

        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.handleQuickView(btn);
        });
      });
    },

    /**
     * Handle add to cart click
     * @param {Element} button
     */
    handleAddToCart: function(button) {
      const variantId = button.getAttribute('data-variant-id');

      if (!variantId || button.classList.contains(this.classes.loading)) {
        return;
      }

      // Show loading state
      button.classList.add(this.classes.loading);
      button.disabled = true;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: parseInt(variantId, 10),
          quantity: 1
        })
      })
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to add to cart');
        }
        return response.json();
      })
      .then(data => {
        // Show success state
        button.classList.remove(this.classes.loading);
        button.classList.add(this.classes.success);

        // Update cart and open drawer
        this.updateCart();

        // Reset button after delay
        setTimeout(() => {
          button.classList.remove(this.classes.success);
          button.disabled = false;
        }, 1500);
      })
      .catch(error => {
        console.error('Error adding to cart:', error);

        // Reset button on error
        button.classList.remove(this.classes.loading);
        button.disabled = false;

        if (window.Toast) {
          window.Toast.show('Unable to add item to cart. Please try again.', 'error');
        }
      });
    },

    /**
     * Handle quick view click
     * @param {Element} button
     */
    handleQuickView: function(button) {
      const productUrl = button.getAttribute('data-product-url');
      const productHandle = button.getAttribute('data-product-handle');
      const hideBuyButtons = button.getAttribute('data-hide-buy-buttons') === 'true';

      // Check if QuickView drawer exists
      if (window.QuickView && typeof window.QuickView.open === 'function') {
        window.QuickView.open(productHandle, { hideBuyButtons: hideBuyButtons });
      } else {
        // Fallback: navigate to product page
        if (productUrl) {
          window.location.href = productUrl;
        }
      }
    },

    /**
     * Update cart count and optionally open cart drawer
     */
    updateCart: function() {
      // Use global CartDrawer if available
      if (window.CartDrawer) {
        window.CartDrawer.fetchCart().then(() => {
          window.CartDrawer.updateHeaderCartCount();
          window.CartDrawer.open();
        });
        return;
      }

      // Fallback: update cart count manually
      fetch('/cart.js', {
        headers: {
          'Accept': 'application/json'
        }
      })
      .then(response => response.json())
      .then(cart => {
        document.querySelectorAll('[data-cart-count]').forEach(el => {
          el.textContent = cart.item_count;
          el.hidden = cart.item_count === 0;
        });
      })
      .catch(error => {
        console.error('Error fetching cart:', error);
      });
    },

    /**
     * Reinitialize for dynamically added content
     * @param {Element} scope - Container with new product cards
     */
    reinit: function(scope) {
      this.init(scope);
    }
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => ProductCard.init());
  } else {
    ProductCard.init();
  }

  // Expose globally for reinit and extensions
  window.ProductCard = ProductCard;

})();
